import { useMemo, useState } from "react";
import { Badge, Box, Collapse, Flex, Icon, Spinner, Text, VStack } from "@chakra-ui/react";
import { ChevronDownIcon, ChevronUpIcon } from "@chakra-ui/icons";
import { useQuery } from "@tanstack/react-query";
import { documentFormatter } from "@/utils/documentFormatter";
import {
  BuscarCcbsEmpregadorParams,
  IVinculoEmpregadorRetorno,
  IVinculosAtivosEmpregadorRetorno,
  RecorteVinculosEmpregador,
} from "@/models/consignado/proposal-billings.model";
import { buscarCcbsAtivasPorEmpregador } from "@/services/consignado/proposta-cobranca.service";
import { hidratarCcbAtivo } from "./hidratarCcbAtivo";
import CcbRow from "./CcbRow";
import { EMPREGADOR_VINCULOS_QUERY_KEY } from "./queryKeys";
import { renderCompetenciaAAAAMM } from "./renderMesCompetencia";

export type FiltrosCcbsEmpregador = Pick<
  BuscarCcbsEmpregadorParams,
  "order" | "ordenar_por" | "tomador" | "codigo_proposta" | "recorte" | "status_negociacao"
>;

// Com filtro rápido ativo, o número do card conta só os contratos daquele
// recorte — o texto diz qual, pra não ser lido como o total da empresa.
const SUFIXO_DO_RECORTE: Partial<Record<RecorteVinculosEmpregador, [string, string]>> = {
  escriturados: ["escriturado", "escriturados"],
  nao_escriturados: ["não escriturado", "não escriturados"],
  repassado: ["repassado", "repassados"],
  nao_repassado: ["não repassado", "não repassados"],
};

const rotuloContratos = (quantidade: number, recorte?: RecorteVinculosEmpregador) => {
  const singular = quantidade === 1;
  const sufixo = recorte ? SUFIXO_DO_RECORTE[recorte] : undefined;
  const contratos = singular ? "contrato" : "contratos";

  return sufixo ? `${quantidade} ${contratos} ${sufixo[singular ? 0 : 1]}` : `${quantidade} ${contratos}`;
};

type Props = {
  empregador: IVinculoEmpregadorRetorno;
  filtros: FiltrosCcbsEmpregador;
};

export default function EmpresaRow({ empregador, filtros }: Props) {
  const [expanded, setExpanded] = useState(false);

  // Nível 2 REAL — carregado sob demanda na 1ª expansão, com os mesmos filtros
  // do nível 1. `contratos_ativos` já é a contagem sob esses filtros, então
  // pedir esse limite traz todas as CCBs do card numa página só.
  const params: BuscarCcbsEmpregadorParams = useMemo(
    () => ({ ...filtros, pagina: 1, limite: Math.max(empregador.contratos_ativos, 1) }),
    [filtros, empregador.contratos_ativos]
  );

  const { data, isLoading } = useQuery<IVinculosAtivosEmpregadorRetorno>({
    queryKey: [EMPREGADOR_VINCULOS_QUERY_KEY, empregador.cnpj, params],
    queryFn: () => buscarCcbsAtivasPorEmpregador(empregador.cnpj, params),
    enabled: expanded,
    retry: 0,
    refetchOnWindowFocus: false,
  });

  const ccbs = useMemo(() => (data?.data ?? []).map(hidratarCcbAtivo), [data]);

  return (
    <Box borderWidth="1px" borderColor="gray.200" borderRadius="md" bg="gray.50">
      <Flex
        as="button"
        onClick={() => setExpanded((prev) => !prev)}
        width="100%"
        alignItems="center"
        justifyContent="space-between"
        gap={3}
        px={4}
        py={3}
        _hover={{ bg: "gray.100" }}
      >
        <Flex alignItems="center" gap={3}>
          <Icon as={expanded ? ChevronUpIcon : ChevronDownIcon} color="gray.500" />
          <Text fontSize="md" fontWeight="bold" color="gray.700">
            {empregador.nome_empregador} - {documentFormatter(empregador.cnpj)}
          </Text>
        </Flex>

        <Flex alignItems="center" gap={2} flexWrap="wrap" justifyContent="flex-end">
          {empregador.competencia != null && (
            <Badge
              colorScheme="gray"
              borderRadius="full"
              px={4}
              py={1.5}
              fontSize="sm"
              fontWeight="normal"
              textTransform="none"
            >
              Competência {renderCompetenciaAAAAMM(empregador.competencia)}
            </Badge>
          )}
          {/* Só vem na ordenação por competência em atraso (TAR-ADM-cobranca-087). */}
          {/* {empregador.competencia_atraso_mais_antiga != null && (
            <Badge
              colorScheme="red"
              borderRadius="full"
              px={4}
              py={1.5}
              fontSize="sm"
              fontWeight="normal"
              textTransform="none"
            >
              Atraso desde {renderCompetenciaAAAAMM(empregador.competencia_atraso_mais_antiga)}
            </Badge>
          )} */}
          <Badge
            bg="primary"
            color="white"
            borderRadius="full"
            px={4}
            py={1.5}
            fontSize="sm"
            fontWeight="bold"
            textTransform="none"
          >
            {rotuloContratos(empregador.contratos_ativos, filtros.recorte)}
          </Badge>
        </Flex>
      </Flex>

      <Collapse in={expanded} animateOpacity>
        <VStack align="stretch" spacing={3} px={4} pb={4}>
          {isLoading && (
            <Flex justifyContent="center" py={4}>
              <Spinner color="primary" />
            </Flex>
          )}
          {!isLoading && ccbs.length === 0 && (
            <Text fontSize="sm" color="gray.500" py={2}>
              Nenhum contrato deste empregador atende aos filtros selecionados.
            </Text>
          )}
          {ccbs.map((ccb) => (
            <CcbRow key={ccb.id} ccb={ccb} />
          ))}
        </VStack>
      </Collapse>
    </Box>
  );
}
